import { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Shell from "../components/Shell.jsx";
import { api } from "../lib/api.js";
import { centsToDisplay, dollarsToCents } from "../lib/money.js";
import { localToday, localNowHM, fmt12 } from "../lib/dates.js";
import { Field, Segmented, SelectWithOther, SaveButton, inputCls } from "../components/ui.jsx";

const STAGES = ["inquiry", "proposal", "booked", "delivered", "paid", "lost"];
const STAGE_LABELS = { inquiry: "Inquiry", proposal: "Proposal", booked: "Booked", delivered: "Delivered", paid: "Paid", lost: "Lost" };
const OFFERINGS = ["Workshop", "Bootcamp", "Custom", "Other"];
export const TASK_TYPES = [
  ["follow_up", "🔔", "Follow-up"],
  ["call", "📞", "Call"],
  ["meeting", "🤝", "Meeting"],
  ["email", "✉️", "Email"],
  ["todo", "✅", "To-do"],
];
export const TASK_ICON = Object.fromEntries(TASK_TYPES.map(([v, icon]) => [v, icon]));
// Tasks first: opening the CRM leads with what needs doing, then people,
// then the pipeline for when it's wanted.
const TABS = [
  { value: "tasks", label: "Tasks" },
  { value: "contacts", label: "Contacts" },
  { value: "pipeline", label: "Pipeline" },
];

export default function Crm() {
  // Deep-linkable: /crm?tab=contacts&q=Capstone%20Rail lands on that company's people.
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState(TABS.some((t) => t.value === params.get("tab")) ? params.get("tab") : "tasks");
  const [dealNonce, setDealNonce] = useState(0);

  // Same-route navigations (the Add menu) only change the search string, so the
  // useState initializers above never re-run — re-sync on every navigation
  // (location.key changes even for an identical URL). ?new=1 is one-shot:
  // consume it, then strip it so tab switches/reloads don't re-open the form.
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    const t = p.get("tab");
    if (t && TABS.some((x) => x.value === t)) setTab(t);
    if (p.get("new") === "1") {
      setTab("pipeline");
      setDealNonce((n) => n + 1);
      p.delete("new");
      setParams(p, { replace: true });
    }
  }, [location.key]);

  return (
    <Shell title="CRM">
      <Segmented options={TABS} value={tab} onChange={setTab} />
      {tab === "pipeline" && <Pipeline dealNonce={dealNonce} />}
      {tab === "contacts" && <Contacts initialQ={params.get("q") || ""} />}
      {tab === "tasks" && <Tasks />}
    </Shell>
  );
}

/* ---------------- Pipeline ---------------- */
function Pipeline({ dealNonce = 0 }) {
  const navigate = useNavigate();
  const [deals, setDeals] = useState(null);
  const [adding, setAdding] = useState(false);

  // Each Add-menu "Add deal" tap bumps the nonce — works even when already mounted.
  useEffect(() => {
    if (dealNonce) setAdding(true);
  }, [dealNonce]);

  const load = () => api("/api/crm/deals").then((d) => setDeals(d.deals)).catch(() => setDeals([]));
  useEffect(() => {
    load();
  }, []);

  async function move(deal, stage) {
    setDeals((ds) => ds.map((d) => (d.id === deal.id ? { ...d, stage } : d)));
    await api(`/api/crm/deals/${deal.id}`, { method: "PUT", body: { stage } }).catch(load);
  }

  if (deals === null) return <p className="text-center text-sm text-slate-400">Loading…</p>;
  const open = deals.filter((d) => d.stage !== "paid" && d.stage !== "lost");
  const pipelineValue = open.reduce((s, d) => s + (d.value_cents || 0), 0);

  return (
    <>
      <div className="flex items-center justify-between rounded-2xl bg-white p-4 shadow-sm">
        <div>
          <p className="text-xs text-slate-400">Open pipeline</p>
          <p className="text-xl font-semibold text-navy-700">{centsToDisplay(pipelineValue)}</p>
        </div>
        <button onClick={() => setAdding(true)} className="rounded-xl bg-navy-700 px-4 py-2 text-sm font-semibold text-white">
          + Engagement
        </button>
      </div>

      {adding && <DealForm onDone={() => { setAdding(false); load(); }} onCancel={() => setAdding(false)} />}

      {STAGES.map((stage) => {
        const inStage = deals.filter((d) => d.stage === stage);
        if (!inStage.length) return null;
        return (
          <section key={stage}>
            <p className="mb-1 flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-slate-400">
              <span>{STAGE_LABELS[stage]}</span>
              <span>{centsToDisplay(inStage.reduce((s, d) => s + (d.value_cents || 0), 0))}</span>
            </p>
            <div className="space-y-2">
              {inStage.map((d) => (
                <div key={d.id} className="rounded-xl bg-white p-3 shadow-sm">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate font-medium text-slate-800">{d.title}</p>
                      <p className="truncate text-xs text-slate-400">
                        {d.contact_name || "No contact"}
                        {d.offering_tag ? ` · ${d.offering_tag}` : ""}
                      </p>
                    </div>
                    <p className="shrink-0 text-sm font-semibold text-slate-700">{centsToDisplay(d.value_cents)}</p>
                  </div>
                  <select
                    value={d.stage}
                    onChange={(e) => move(d, e.target.value)}
                    className="mt-2 w-full rounded-lg border border-slate-200 bg-slate-50 px-2 py-1.5 text-xs"
                  >
                    {STAGES.map((s) => (
                      <option key={s} value={s}>
                        Move to: {STAGE_LABELS[s]}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </section>
        );
      })}
      {deals.length === 0 && (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center">
          <p className="text-4xl">🧭</p>
          <p className="mt-3 font-medium text-slate-700">No engagements yet</p>
          <p className="mt-1 text-sm text-slate-500">Add an engagement to start tracking your pipeline.</p>
        </div>
      )}
    </>
  );
}

function DealForm({ onDone, onCancel }) {
  const [title, setTitle] = useState("");
  // The picked contact is held whole, not looked up in a list — a contact
  // created from inside the picker isn't in any list that was fetched earlier.
  const [picked, setPicked] = useState(null);
  const [value, setValue] = useState("");
  const [offering, setOffering] = useState("Workshop");
  const [stage, setStage] = useState("inquiry");
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!title.trim()) return;
    setBusy(true);
    await api("/api/crm/deals", {
      method: "POST",
      body: {
        title,
        contact_id: picked?.id || null,
        value_cents: dollarsToCents(value || "0") || 0,
        offering_tag: offering,
        stage,
      },
    });
    onDone();
  }

  return (
    <form onSubmit={submit} className="space-y-3 rounded-2xl bg-white p-4 shadow-sm">
      <Field label="Engagement title">
        <input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls} placeholder="e.g. Acme Corp full-day workshop" />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Value">
          <input inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)} className={inputCls} placeholder="$0.00" />
        </Field>
        <Field label="Offering">
          <SelectWithOther value={offering} onChange={setOffering} options={OFFERINGS} />
        </Field>
      </div>
      <Field label="Client">
        {/* Was every contact in one scrolling list. Search and pick, or add
            someone who isn't in the CRM yet without leaving the form. */}
        <ContactPicker value={picked} onChange={setPicked} placeholder="Client (name or company)…" allowCreate />
      </Field>
      <Field label="Stage">
        <select value={stage} onChange={(e) => setStage(e.target.value)} className={inputCls}>
          {STAGES.map((s) => (
            <option key={s} value={s}>
              {STAGE_LABELS[s]}
            </option>
          ))}
        </select>
      </Field>
      <div className="flex gap-2">
        <button type="button" onClick={onCancel} className="flex-1 rounded-xl border border-slate-300 py-3 text-sm font-semibold text-slate-500">
          Cancel
        </button>
        <div className="flex-1">
          <SaveButton busy={busy}>Add engagement</SaveButton>
        </div>
      </div>
    </form>
  );
}

/* ---------------- Contacts ---------------- */
const CONTACT_SORTS = [
  ["last", "Last name"],
  ["first", "First name"],
  ["company", "Company"],
  ["added", "Recently added"],
];

function Contacts({ initialQ = "" }) {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState(null);
  const [q, setQ] = useState(initialQ);
  const [type, setType] = useState("");
  const [sort, setSort] = useState("last");

  const load = () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (type) params.set("type", type);
    params.set("sort", sort);
    return api(`/api/crm/contacts?${params}`).then((d) => setContacts(d.contacts)).catch(() => setContacts([]));
  };
  useEffect(() => {
    const t = setTimeout(load, q ? 250 : 0);
    return () => clearTimeout(t);
  }, [q, type, sort]);

  return (
    <>
      <div className="flex gap-2">
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search contacts…" className={inputCls} />
        <button onClick={() => navigate("/crm/new")} className="shrink-0 rounded-xl bg-navy-700 px-4 text-sm font-semibold text-white">
          +
        </button>
      </div>
      {/* Scrollable chip row — six types no longer fit a fixed segmented control */}
      <div className="-mx-5 flex gap-2 overflow-x-auto px-5 pb-1">
        {[
          ["", "All"],
          ["lead", "Leads"],
          ["client", "Clients"],
          ["vendor", "Vendors"],
          ["contractor", "Contractors"],
          ["investor", "Investors"],
        ].map(([v, label]) => (
          <button
            key={v}
            onClick={() => setType(v)}
            className={`shrink-0 rounded-full px-3.5 py-1.5 text-sm font-semibold ${
              type === v ? "bg-navy-700 text-white" : "bg-white text-slate-500 shadow-sm"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <span className="shrink-0 text-xs font-semibold uppercase tracking-wide text-slate-400">Sort</span>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className="flex-1 rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm text-slate-600"
        >
          {CONTACT_SORTS.map(([v, label]) => (
            <option key={v} value={v}>
              {label}
            </option>
          ))}
        </select>
      </div>
      {contacts === null && <p className="text-center text-sm text-slate-400">Loading…</p>}
      {contacts?.length === 0 && <p className="py-6 text-center text-sm text-slate-400">No contacts{q || type ? " match" : " yet"}.</p>}
      <div className="divide-y divide-slate-100 rounded-2xl bg-white shadow-sm">
        {(contacts || []).map((c) => (
          <button key={c.id} onClick={() => navigate(`/crm/${c.id}`)} className="flex w-full items-center justify-between px-4 py-3 text-left">
            <div className="min-w-0">
              <p className="truncate font-medium text-slate-800">{c.name}</p>
              <p className="truncate text-xs text-slate-400">
                {c.company || c.email || "—"}
                {c.deal_count ? ` · ${c.deal_count} engagement${c.deal_count > 1 ? "s" : ""}` : ""}
              </p>
            </div>
            <span className="ml-2 shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-slate-500">{c.type}</span>
          </button>
        ))}
      </div>
    </>
  );
}

/* ---------------- Tasks ---------------- */
// Search-as-you-type contact selector; selected contact shows as a removable chip.
// `allowCreate` stays opt-in although all six call sites now pass it. It is a
// side-effecting capability — the picker writes a contact — and a future caller
// should have to ask for that rather than inherit it by default. It was missing
// from five of the six, which left the operator typing a client's name into an
// invoice with nothing to select and no way to save.
export function ContactPicker({ value, onChange, placeholder = "Link a contact (name or company)…", allowCreate = false }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [creating, setCreating] = useState(false);

  // Meeting someone new is the normal case when a deal gets logged, so the
  // search doubles as the way to add them — no detour through the contacts page
  // and back with a half-filled form waiting.
  async function createFromQuery() {
    const name = q.trim().slice(0, 120);
    if (!name || creating) return;
    setCreating(true);
    try {
      const d = await api("/api/crm/contacts", { method: "POST", body: { name, type: "lead" } });
      const id = d?.id || d?.contact?.id;
      if (id) {
        onChange({ id, name, company: "" });
        setQ("");
        setResults([]);
      }
    } catch {
      /* leave the typed text in place so nothing is lost */
    }
    setCreating(false);
  }

  useEffect(() => {
    if (!q.trim()) {
      setResults([]);
      return;
    }
    const t = setTimeout(() => {
      api(`/api/crm/contacts?q=${encodeURIComponent(q)}`)
        .then((d) => setResults((d.contacts || []).slice(0, 6)))
        .catch(() => setResults([]));
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  if (value)
    return (
      <div className="flex items-center justify-between rounded-xl bg-navy-700/5 px-3 py-2.5">
        <span className="truncate text-sm font-medium text-navy-700">👤 {value.name}</span>
        <button type="button" onClick={() => onChange(null)} className="ml-2 shrink-0 px-1 text-slate-400">
          ✕
        </button>
      </div>
    );

  return (
    <div>
      <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={placeholder} className={inputCls} />
      {results.length > 0 && (
        <div className="mt-1 divide-y divide-slate-100 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          {results.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => {
                onChange({ id: c.id, name: c.name, company: c.company || "" });
                setQ("");
                setResults([]);
              }}
              className="block w-full px-3 py-2.5 text-left text-sm text-slate-800"
            >
              {c.name}
              {c.company && <span className="text-xs text-slate-400"> · {c.company}</span>}
            </button>
          ))}
        </div>
      )}
      {allowCreate && q.trim().length > 1 && !results.some((c) => c.name.toLowerCase() === q.trim().toLowerCase()) && (
        <button
          type="button"
          onClick={createFromQuery}
          disabled={creating}
          className="mt-1 w-full rounded-xl border border-dashed border-navy-600/40 py-2.5 text-sm font-semibold text-navy-700 disabled:opacity-50"
        >
          {creating ? "Adding…" : `+ Add "${q.trim()}" as a new contact`}
        </button>
      )}
    </div>
  );
}

// Shared due-date presentation: label + overdue flag (used here and on Home).
export function taskDueInfo(t) {
  if (!t.due_date) return { label: "no date", overdue: false };
  const today = localToday();
  const overdue = t.due_date < today || (t.due_date === today && t.due_time && t.due_time < localNowHM());
  const day = t.due_date === today ? "today" : t.due_date;
  return { label: `due ${day}${t.due_time ? ` · ${fmt12(t.due_time)}` : ""}`, overdue };
}

// Reusable "schedule a task" form — used on the Tasks tab and inside a contact record.
export function TaskForm({ contact = null, lockContact = false, onSaved }) {
  const [taskType, setTaskType] = useState("follow_up");
  const [picked, setPicked] = useState(contact);
  const [title, setTitle] = useState("");
  const [due, setDue] = useState("");
  const [time, setTime] = useState("");
  const [priority, setPriority] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function add(e) {
    e.preventDefault();
    if (!title.trim()) return;
    setError("");
    // Follow-ups only point forward: block past dates and past times today.
    const today = localToday();
    if (due && due < today) return setError("That date already passed — pick today or later.");
    if (due === today && time && time <= localNowHM()) return setError("That time already passed today — pick a later time.");
    if (!due && time) return setError("Pick a date for that time.");
    setBusy(true);
    try {
      await api("/api/crm/tasks", {
        method: "POST",
        body: {
          title,
          task_type: taskType,
          due_date: due || null,
          due_time: time || null,
          priority,
          contact_id: picked?.id || null,
        },
      });
      setTitle("");
      setDue("");
      setTime("");
      setPriority(0);
      if (!lockContact) setPicked(null);
      onSaved?.();
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={add} className="space-y-2 rounded-2xl bg-white p-4 shadow-sm">
      <div className="flex gap-1">
        {TASK_TYPES.map(([v, icon, label]) => (
          <button
            key={v}
            type="button"
            onClick={() => setTaskType(v)}
            className={`flex-1 rounded-lg py-1.5 text-center ${taskType === v ? "bg-navy-700/10" : "bg-slate-50"}`}
          >
            <span className="text-base">{icon}</span>
            <span className="block text-[9px] font-semibold text-slate-500">{label}</span>
          </button>
        ))}
      </div>
      {!lockContact && <ContactPicker value={picked} onChange={setPicked} allowCreate />}
      <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="What needs doing? e.g. Follow-up call re: proposal" className={inputCls} />
      <div className="flex gap-2">
        <input type="date" min={localToday()} value={due} onChange={(e) => setDue(e.target.value)} className={inputCls} />
        <input type="time" value={time} onChange={(e) => setTime(e.target.value)} className={inputCls} />
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setPriority((p) => (p ? 0 : 1))}
          className={`flex-1 rounded-xl py-2.5 text-sm font-semibold ${
            priority ? "bg-red-50 text-red-600 ring-1 ring-red-200" : "bg-slate-50 text-slate-500"
          }`}
        >
          {priority ? "🔴 High priority" : "⚑ Mark high priority"}
        </button>
        <button disabled={busy} className="flex-1 rounded-xl bg-navy-700 text-sm font-semibold text-white disabled:opacity-50">
          Add
        </button>
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
    </form>
  );
}

function Tasks() {
  const navigate = useNavigate();
  const [view, setView] = useState("open"); // open | done
  const [tasks, setTasks] = useState(null);
  const [q, setQ] = useState("");

  const load = (v = view) => {
    setTasks(null);
    return api(`/api/crm/tasks?status=${v}`).then((d) => setTasks(d.tasks)).catch(() => setTasks([]));
  };
  useEffect(() => {
    load(view);
  }, [view]);

  async function toggle(t) {
    await api(`/api/crm/tasks/${t.id}`, { method: "PUT", body: { done: !t.done } });
    load();
  }

  // Completed history is a memory aid — filter by anything you remember.
  const shown = (tasks || []).filter(
    (t) => !q.trim() || `${t.title} ${t.contact_name || ""}`.toLowerCase().includes(q.trim().toLowerCase()),
  );

  return (
    <>
      <Segmented
        options={[
          { value: "open", label: "Open" },
          { value: "done", label: "✅ Completed" },
        ]}
        value={view}
        onChange={setView}
      />
      {view === "open" && <TaskForm onSaved={load} />}
      {view === "done" && (
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search what you did, or who for…"
          className={inputCls}
        />
      )}

      {tasks === null && <p className="text-center text-sm text-slate-400">Loading…</p>}
      {tasks?.length === 0 && (
        <p className="py-6 text-center text-sm text-slate-400">
          {view === "open" ? "No open follow-ups 🎉" : "Nothing completed yet."}
        </p>
      )}
      {tasks?.length > 0 && shown.length === 0 && (
        <p className="py-6 text-center text-sm text-slate-400">No completed tasks match.</p>
      )}
      <div className="divide-y divide-slate-100 rounded-2xl bg-white shadow-sm">
        {shown.map((t) => (
          <div key={t.id} className="flex items-center gap-3 px-4 py-3">
            <button onClick={() => toggle(t)} className="shrink-0 text-xl" title={t.done ? "Reopen" : "Complete"}>
              {t.done ? "✅" : "⬜️"}
            </button>
            <div className="min-w-0 flex-1" onClick={() => t.contact_id && navigate(`/crm/${t.contact_id}`)}>
              <p className={`truncate text-sm ${t.done ? "text-slate-500" : "font-medium text-slate-800"}`}>
                {t.priority ? "🔴 " : ""}
                {TASK_ICON[t.task_type] || "🔔"} {t.title}
              </p>
              {t.done ? (
                <p className="truncate text-xs text-slate-400">
                  {t.contact_name ? `${t.contact_name} · ` : ""}
                  done {t.completed_at ? t.completed_at.slice(0, 10) : ""}
                </p>
              ) : (
                (() => {
                  const d = taskDueInfo(t);
                  return (
                    <p className={`truncate text-xs ${d.overdue ? "font-semibold text-red-600" : "text-slate-400"}`}>
                      {t.contact_name ? `${t.contact_name} · ` : ""}
                      {d.label}
                      {d.overdue ? " — overdue" : ""}
                    </p>
                  );
                })()
              )}
            </div>
            {t.link && (
              <a
                href={t.link}
                target="_blank"
                rel="noreferrer noopener"
                onClick={(e) => e.stopPropagation()}
                className="shrink-0 rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600"
              >
                Open ↗
              </a>
            )}
            {t.contact_id && <span className="shrink-0 text-slate-300">›</span>}
          </div>
        ))}
      </div>
    </>
  );
}
